import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { ShoppingBag, Euro, TrendingUp, Bell, ArrowRight, CheckCircle2, Circle } from 'lucide-react';
import { spacing, borderRadius, fontSize, fontWeight } from '../../styles/theme';
import WebvonkLoader from '../components/WebvonkLoader';
import { getKmfcClient } from '../../lib/supabase';
import { SHOP_STATUS_EVENT, fetchShopStatus } from '../lib/shopStatus';
import { usePermissions } from '../hooks/usePermissions';
import DataSyncService from '../services/DataSyncService';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  height: calc(100vh - 52px);
  background: ${p => p.theme.colors.background};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 28px 28px 40px;
  animation: ${fadeUp} 0.3s ease;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  margin: 0 0 4px;
`;

const PageSub = styled.div`
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
`;

/* ── Layout grid ── */
const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 20px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Left = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

/* ── Cards ── */
const Card = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 16px;
  padding: 20px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
`;

const CardSub = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  margin-top: 2px;
`;

const EmptyText = styled.div`
  font-size: 13px;
  color: ${p => p.theme.colors.muted};
  padding: 12px 0;
`;

const CardAction = styled.button`
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.theme.colors.brand};
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  opacity: 0.8;
  &:hover { opacity: 1; }
`;

/* ── Stat row ── */
const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: ${p => p.theme.colors.surface};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: 14px;
  padding: 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const StatTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${p => p.$color}18;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.$color};
`;

const StatLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${p => p.theme.colors.muted};
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  letter-spacing: -0.02em;
  line-height: 1;
`;

const StatChange = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.$up ? '#22c55e' : p.theme.colors.muted};
`;

/* ── Alert banner ── */
const AlertBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: rgba(249,115,22,0.08);
  border: 1px solid rgba(249,115,22,0.2);
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba(249,115,22,0.12); }
`;

const AlertText = styled.div`
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: #f97316;
`;

/* ── Order history list ── */
const OrderList = styled.div`
  display: flex;
  flex-direction: column;
`;

const OrderItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid ${p => p.theme.colors.border};
  cursor: pointer;
  &:last-child { border-bottom: none; }
  &:hover { opacity: 0.8; }
`;

const OrderAvatar = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${p => p.$color || p.theme.colors.brand};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  flex-shrink: 0;
`;

const OrderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const OrderName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const OrderMeta = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  margin-top: 1px;
`;

const OrderRight = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const OrderAmount = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
`;

const OrderTime = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
  margin-top: 1px;
`;

/* ── Status indicator ── */
const StatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  &:last-child { margin-bottom: 0; }
`;

const StatusLabel = styled.div`
  font-size: 13px;
  color: ${p => p.theme.colors.text};
  font-weight: 500;
  flex: 1;
`;

const StatusBar = styled.div`
  width: 100px;
  height: 4px;
  border-radius: 99px;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  overflow: hidden;
`;

const StatusFill = styled.div`
  height: 100%;
  width: ${p => p.$pct}%;
  border-radius: 99px;
  background: ${p => p.$color};
`;

const StatusPct = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.theme.colors.muted};
  width: 32px;
  text-align: right;
`;

/* ── Shop status pill ── */
const ShopPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${p => p.$open ? 'rgba(34,197,94,0.12)' : 'rgba(249,115,22,0.12)'};
  color: ${p => p.$open ? '#22c55e' : '#f97316'};
`;

const Dot = styled.div`
  width: 6px; height: 6px; border-radius: 50%;
  background: currentColor;
`;

/* ── Hourly chart ── */
const HourlyChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  height: 60px;
`;

const HourlyBar = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  height: 100%;
  justify-content: flex-end;
`;

const HourlyFill = styled.div`
  width: 100%;
  border-radius: 4px 4px 0 0;
  background: ${p => p.$active ? p.theme.colors.brand : (p.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)')};
  height: ${p => p.$pct}%;
  min-height: ${p => p.$count > 0 ? 4 : 2}px;
  transition: height 0.3s ease;
`;

const HourlyLabel = styled.div`
  font-size: 9px;
  color: ${p => p.theme.colors.muted};
  opacity: 0.6;
`;

const HourlyFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  font-size: 11px;
  color: ${p => p.theme.colors.muted};
`;



const AVATAR_COLORS = ['#f97316','#3b82f6','#8b5cf6','#22c55e','#ef4444','#ec4899','#06b6d4','#84cc16'];

const VALID_STATUSES = ['nieuw', 'bereiding', 'klaar', 'onderweg', 'bezorgd', 'voltooid'];

const STATUS_COLORS = {
  nieuw: '#f97316', bereiding: '#eab308', klaar: '#3b82f6',
  onderweg: '#8b5cf6', bezorgd: '#22c55e', voltooid: '#22c55e',
};

const STATUS_LABELS = {
  nieuw: 'Nieuw', bereiding: 'Bereiding', klaar: 'Klaar',
  onderweg: 'Onderweg', bezorgd: 'Voltooid', voltooid: 'Voltooid',
};

// Statussen die in de activiteitsbars worden getoond (bezorgd gemerged in voltooid)
const ACTIVITY_STATUSES = ['nieuw', 'bereiding', 'klaar', 'onderweg', 'voltooid'];

export default function DashboardScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ newOrders: 0, todayOrders: 0, todayRevenue: 0, completedToday: 0, todayTips: 0, statusCounts: {} });
  const [shopOpen, setShopOpen] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [hourlyData, setHourlyData] = useState([]);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    const handler = (e) => setShopOpen(!!e.detail?.isOpen);
    window.addEventListener(SHOP_STATUS_EVENT, handler);
    return () => window.removeEventListener(SHOP_STATUS_EVENT, handler);
  }, []);

  useEffect(() => {
    loadAll();
    const unsub = DataSyncService.subscribe('orders', () => loadAll());
    return unsub;
  }, []);

  const loadAll = async () => {
    await Promise.all([loadStats(), loadShopStatus(), loadRecentOrders(), loadHourly()]);
    setLoading(false);
  };

  const loadStats = async () => {
    const kmfcClient = getKmfcClient();
    if (!kmfcClient) return;
    const today = new Date().toISOString().split('T')[0];
    const { data } = await kmfcClient
      .from('orders')
      .select('status, total_cents, tip_cents')
      .gte('placed_at', today)
      .in('status', VALID_STATUSES);
    if (!data) return;
    const statusCounts = {};
    VALID_STATUSES.forEach(s => { statusCounts[s] = data.filter(o => o.status === s).length; });
    // bezorgd telt als voltooid
    const mergedCounts = { ...statusCounts };
    mergedCounts['voltooid'] = (statusCounts['voltooid'] || 0) + (statusCounts['bezorgd'] || 0);
    setStats({
      newOrders: statusCounts['nieuw'] || 0,
      todayOrders: data.length,
      todayRevenue: data.reduce((s, o) => s + (o.total_cents || 0), 0),
      completedToday: mergedCounts['voltooid'],
      todayTips: data.reduce((s, o) => s + (o.tip_cents || 0), 0),
      statusCounts: mergedCounts,
    });
  };

  const loadHourly = async () => {
    const kmfcClient = getKmfcClient();
    if (!kmfcClient) return;
    const today = new Date().toISOString().split('T')[0];
    const { data } = await kmfcClient
      .from('orders')
      .select('placed_at, total_cents')
      .gte('placed_at', today)
      .in('status', VALID_STATUSES);
    if (!data) return;
    const buckets = Array.from({ length: 24 }, (_, h) => ({ hour: h, count: 0, revenue: 0 }));
    data.forEach(o => {
      const h = new Date(o.placed_at).getHours();
      buckets[h].count++;
      buckets[h].revenue += o.total_cents || 0;
    });
    // Toon alleen uren van 10:00 tot nu
    const nowH = new Date().getHours();
    setHourlyData(buckets.slice(10, Math.max(nowH + 1, 11)));
  };

  const loadShopStatus = async () => {
    const result = await fetchShopStatus();
    setShopOpen(result.isOpen);
  };

  const loadRecentOrders = async () => {
    const kmfcClient = getKmfcClient();
    if (!kmfcClient) return;
    const { data } = await kmfcClient
      .from('orders')
      .select('id, status, customer_name, total_cents, placed_at, order_code')
      .in('status', VALID_STATUSES)
      .order('placed_at', { ascending: false })
      .limit(8);
    setRecentOrders(data || []);
  };

  const fmtEUR = (cents) => `€ ${((cents || 0) / 100).toFixed(2).replace('.', ',')}`;
  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '';
  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const time = d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    const today = new Date(); today.setHours(0,0,0,0);
    const day = new Date(d); day.setHours(0,0,0,0);
    const diff = Math.round((today - day) / 86400000);
    const prefix = diff === 0 ? 'Vandaag' : diff === 1 ? 'Gisteren' : diff === 2 ? 'Eergisteren' : d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
    return `${prefix} ${time}`;
  };
  const initials = (name) => (name || 'G').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const avatarColor = (name) => AVATAR_COLORS[(name || '').charCodeAt(0) % AVATAR_COLORS.length];

  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Goedemorgen' : now.getHours() < 18 ? 'Goedemiddag' : 'Goedenavond';

  if (loading) return <WebvonkLoader text="Dashboard wordt geladen..." />;

  const totalActivity = stats.todayOrders || 1;

  return (
    <Container>
      <Content>
        <PageHeader>
          <div style={{ marginBottom: 4 }}>
            <PageTitle>{greeting}</PageTitle>
          </div>
          <PageSub>Hier is een overzicht van vandaag</PageSub>
        </PageHeader>

        {stats.newOrders > 0 && (
          <AlertBanner onClick={() => navigate('/KMFC/orders')} style={{ marginBottom: 20 }}>
            <Bell size={16} color="#f97316" />
            <AlertText>{stats.newOrders} nieuwe bestelling{stats.newOrders !== 1 ? 'en' : ''} wacht{stats.newOrders === 1 ? '' : 'en'} op acceptatie</AlertText>
            <ArrowRight size={15} color="#f97316" />
          </AlertBanner>
        )}

        <StatRow style={{ marginBottom: 20 }}>
          <StatCard>
            <StatTop>
              <StatLabel>Bestellingen</StatLabel>
              <StatIcon $color="#f97316"><ShoppingBag size={16} /></StatIcon>
            </StatTop>
            <StatValue>{stats.todayOrders}</StatValue>
            <StatChange>Vandaag</StatChange>
          </StatCard>
          <StatCard>
            <StatTop>
              <StatLabel>Omzet</StatLabel>
              <StatIcon $color="#22c55e"><Euro size={16} /></StatIcon>
            </StatTop>
            <StatValue style={{ fontSize: stats.todayRevenue >= 100000 ? '18px' : '24px' }}>{fmtEUR(stats.todayRevenue)}</StatValue>
            <StatChange>Vandaag</StatChange>
          </StatCard>
          <StatCard>
            <StatTop>
              <StatLabel>Fooi</StatLabel>
              <StatIcon $color="#eab308"><TrendingUp size={16} /></StatIcon>
            </StatTop>
            <StatValue style={{ fontSize: stats.todayTips >= 100000 ? '18px' : '24px' }}>{fmtEUR(stats.todayTips)}</StatValue>
            <StatChange>Vandaag</StatChange>
          </StatCard>
        </StatRow>

        <Grid>
          <Left>
            {/* Recente orders */}
            <Card>
              <CardHeader>
                <CardTitle>Recente bestellingen</CardTitle>
                <CardAction onClick={() => navigate('/KMFC/orders')}>
                  Bekijk alles <ArrowRight size={12} />
                </CardAction>
              </CardHeader>
              <OrderList>
                {recentOrders.length === 0 && (
                  <EmptyText style={{ textAlign: 'center', padding: '24px 0' }}>Nog geen bestellingen vandaag</EmptyText>
                )}
                {recentOrders.map((order) => (
                  <OrderItem key={order.id} onClick={() => navigate('/KMFC/orders')}>
                    <OrderAvatar $color={avatarColor(order.customer_name)}>
                      {initials(order.customer_name)}
                    </OrderAvatar>
                    <OrderInfo>
                      <OrderName>{order.customer_name || 'Gast'}</OrderName>
                      <OrderMeta style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {order.order_code ? `#${order.order_code.split('-').pop()}` : '#???'}
                        <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: STATUS_COLORS[order.status] || '#9ca3af' }} />
                        <span style={{ color: STATUS_COLORS[order.status] || '#9ca3af' }}>{STATUS_LABELS[order.status] || order.status}</span>
                      </OrderMeta>
                    </OrderInfo>
                    <OrderRight>
                      <OrderAmount>{fmtEUR(order.total_cents)}</OrderAmount>
                      <OrderTime>{fmtDate(order.placed_at)}</OrderTime>
                    </OrderRight>
                  </OrderItem>
                ))}
              </OrderList>
            </Card>
          </Left>

          <Right>
            {/* Activiteit */}
            <Card>
              <CardHeader>
                <div>
                  <CardTitle>Live activiteit</CardTitle>
                  <CardSub>Vandaag</CardSub>
                </div>
              </CardHeader>
              {ACTIVITY_STATUSES.map(s => {
                const count = stats.statusCounts?.[s] || 0;
                const pct = Math.round(count / totalActivity * 100);
                return (
                  <StatusRow key={s}>
                    <Circle size={10} style={{ color: STATUS_COLORS[s], flexShrink: 0 }} />
                    <StatusLabel>{STATUS_LABELS[s]}</StatusLabel>
                    <StatusBar><StatusFill $pct={pct} $color={STATUS_COLORS[s]} /></StatusBar>
                    <StatusPct>{count}</StatusPct>
                  </StatusRow>
                );
              })}
            </Card>

            {/* Bestellingen per uur */}
            <Card>
              <CardHeader>
                <div>
                  <CardTitle>Bestellingen per uur</CardTitle>
                  <CardSub>Vandaag</CardSub>
                </div>
              </CardHeader>
              {hourlyData.length > 0 ? (() => {
                const nowH = new Date().getHours();
                const maxCount = Math.max(...hourlyData.map(b => b.count), 1);
                return (
                  <>
                    <HourlyChart>
                      {hourlyData.map(b => (
                        <HourlyBar key={b.hour}>
                          <HourlyFill
                            $pct={Math.max(Math.round(b.count / maxCount * 100), b.count > 0 ? 8 : 3)}
                            $count={b.count}
                            $active={b.hour === nowH}
                          />
                        </HourlyBar>
                      ))}
                    </HourlyChart>
                    <HourlyFooter>
                      <span>{hourlyData[0]?.hour}:00</span>
                      <span style={{ color: 'inherit', opacity: 0.5 }}>{stats.todayOrders} bestellingen</span>
                      <span>{hourlyData[hourlyData.length - 1]?.hour}:00</span>
                    </HourlyFooter>
                  </>
                );
              })() : (
                <EmptyText>Nog geen bestellingen vandaag</EmptyText>
              )}
            </Card>
          </Right>
        </Grid>
      </Content>
    </Container>
  );
}
